/**
 * AIBrainService.js — Centralized AI Brain for all business screens
 * 
 * Usage in any screen:
 *   import { useAI } from '../../services/AIBrainService';
 *   const { ask, agents, loading } = useAI('FinancialManagementScreen');
 *   const response = await ask('Analyse my cash flow', { invoices, expenses });
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  View, Text, TextInput, TouchableOpacity, ScrollView,
  ActivityIndicator, Modal, StyleSheet, KeyboardAvoidingView, Platform,
} from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';

const API_BASE = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
const AI_CACHE_KEY = 'ai_agents_cache';
const CACHE_TTL = 30 * 60 * 1000; // 30 min

// ─── Headers helper ─────────────────────────────────────────────────────────
const getHeaders = async () => {
  const token = await AsyncStorage.getItem('auth_token') || await AsyncStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    'X-API-Key': API_KEY,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

// ─── Fetch company data for context ─────────────────────────────────────────
// Core business tables used when an agent has no knowledge_sources configured,
// so the AI always receives real company data instead of an empty context.
const DEFAULT_CONTEXT_TABLES = ['employees', 'clients', 'projects', 'financial_invoices', 'company_products'];

const fetchCompanyContext = async (tables = [], companyId) => {
  if (!companyId) return {};
  // Fall back to the core tables when the agent specifies no knowledge sources.
  const tableList = (Array.isArray(tables) && tables.length) ? tables : DEFAULT_CONTEXT_TABLES;
  const h = await getHeaders();
  const context = {};
  const fetches = tableList.slice(0, 6).map(async (table) => {
    try {
      const r = await fetch(`${API_BASE}/api/data/${table}?company_id=${companyId}&limit=50`, { headers: h });
      const d = await r.json();
      if (d.success && Array.isArray(d.data) && d.data.length) {
        context[table] = d.data;
      }
    } catch {}
  });
  await Promise.allSettled(fetches);
  return context;
};

// ─── Build context summary from data ────────────────────────────────────────
const buildContextSummary = (data) => {
  const parts = [];
  const flatPairs = [];
  for (const [table, rows] of Object.entries(data)) {
    // Flat scalar values (e.g. screen-supplied extraContext like revenue/customers)
    if (!Array.isArray(rows)) {
      if (rows != null && typeof rows !== 'object') {
        flatPairs.push(`${table}: ${rows}`);
      } else if (rows && typeof rows === 'object') {
        // Nested object — flatten one level
        for (const [k, v] of Object.entries(rows)) {
          if (v != null && typeof v !== 'object') flatPairs.push(`${k}: ${v}`);
        }
      }
      continue;
    }
    if (!rows.length) continue;
    const count = rows.length;
    const sample = rows.slice(0, 3);
    const cols = Object.keys(sample[0] || {}).filter(k =>
      !['id','created_at','updated_at','company_id','business_id'].includes(k)
    ).slice(0, 8);

    parts.push(`\n[${table}] (${count} records):`);
    sample.forEach((row, i) => {
      const vals = cols.map(c => `${c}: ${String(row[c] || '').substring(0, 50)}`).join(', ');
      parts.push(`  ${i + 1}. ${vals}`);
    });

    // Add summary stats
    const numericCols = cols.filter(c => typeof sample[0][c] === 'number' || !isNaN(parseFloat(sample[0][c])));
    numericCols.forEach(c => {
      const sum = rows.reduce((s, r) => s + (parseFloat(r[c]) || 0), 0);
      const avg = sum / count;
      parts.push(`  Summary: total ${c}=£${sum.toLocaleString()}, avg=£${avg.toFixed(0)}`);
    });
  }
  if (flatPairs.length) {
    parts.unshift('[Key Metrics]\n  ' + flatPairs.join('\n  '));
  }
  return parts.join('\n');
};

// ─── Call Claude API ────────────────────────────────────────────────────────
// ─── Call the JobEng AI proxy (server-side Groq) ────────────────────────────
// SECURE: no provider API keys in the app. The previous version called
// api.anthropic.com directly from the device with no key (always failed).
const callClaude = async (systemPrompt, userMessage, model = 'jobeng-ai', maxTokens = 1000, temperature = 0.7) => {
  const startTime = Date.now();
  try {
    const h = await getHeaders();
    const response = await fetch(`${API_BASE}/api/ai`, {
      method: 'POST',
      headers: h,
      body: JSON.stringify({
        prompt: `Instructions: ${systemPrompt}\n\n${userMessage}`,
        max_tokens: maxTokens,
        temperature,
      }),
    });
    const data = await response.json();
    const text = data?.content || data?.message || data?.text || '';
    if (!response.ok || !text) throw new Error(data?.error || `HTTP ${response.status}`);
    const tokensUsed = data?.usage?.total_tokens || 0;
    return {
      text,
      tokensUsed,
      responseTimeMs: Date.now() - startTime,
      model: 'jobeng-ai',
      success: true,
    };
  } catch (error) {
    return {
      text: `I'm currently unavailable. Error: ${error.message}`,
      tokensUsed: 0,
      responseTimeMs: Date.now() - startTime,
      model,
      success: false,
      error: error.message,
    };
  }
};

// ─── Log usage to server ────────────────────────────────────────────────────
const logUsage = async (agentId, screen, companyId, userId, result) => {
  try {
    const h = await getHeaders();
    await fetch(`${API_BASE}/api/data/ai_agent_usage`, {
      method: 'POST',
      headers: h,
      body: JSON.stringify({
        company_id: companyId,
        agent_id: agentId,
        screen,
        user_id: userId,
        tokens_input: result.tokensUsed,
        tokens_output: 0,
        response_time_ms: result.responseTimeMs,
        success: result.success,
        error_message: result.error || null,
      }),
    });
    // Increment usage count
    await fetch(`${API_BASE}/api/data/ai_agents`, {
      method: 'PUT',
      headers: h,
      body: JSON.stringify({
        agent_id: agentId,
        usage_count: 1, // server should increment
        last_used_at: new Date().toISOString(),
      }),
    }).catch(() => {});
  } catch {}
};

// ─── Save conversation ──────────────────────────────────────────────────────
const saveConversation = async (agentId, screen, companyId, userId, userMsg, assistantMsg, model) => {
  try {
    const h = await getHeaders();
    // Save user message
    await fetch(`${API_BASE}/api/data/ai_conversations`, {
      method: 'POST', headers: h,
      body: JSON.stringify({ company_id: companyId, agent_id: agentId, user_id: userId, screen, role: 'user', content: userMsg, model }),
    });
    // Save assistant message
    await fetch(`${API_BASE}/api/data/ai_conversations`, {
      method: 'POST', headers: h,
      body: JSON.stringify({ company_id: companyId, agent_id: agentId, user_id: userId, screen, role: 'assistant', content: assistantMsg, model }),
    });
  } catch {}
};

// ═══════════════════════════════════════════════════════════════════════════════
// PUBLIC API
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Fetch all AI agents (with caching)
 */
export const fetchAgents = async (companyId) => {
  try {
    // Check cache
    const cached = await AsyncStorage.getItem(AI_CACHE_KEY + '_' + (companyId || ''));
    if (cached) {
      const { ts, data } = JSON.parse(cached);
      if (Date.now() - ts < CACHE_TTL) return data;
    }
    // Fetch from server
    const h = await getHeaders();
    const r = await fetch(`${API_BASE}/api/data/ai_agents?enabled=true&limit=50`, { headers: h });
    const d = await r.json();
    const agents = d.success ? (d.data || []) : [];
    // Cache
    await AsyncStorage.setItem(AI_CACHE_KEY + '_' + (companyId || ''), JSON.stringify({ ts: Date.now(), data: agents }));
    return agents;
  } catch {
    return [];
  }
};

/**
 * Get agents for a specific screen
 */
export const getAgentsForScreen = async (screenName, companyId) => {
  const all = await fetchAgents(companyId);
  return all.filter(a => {
    const screens = Array.isArray(a.screens) ? a.screens : [];
    return screens.includes(screenName) || screens.includes('*');
  });
};

/**
 * Ask an AI agent a question with automatic context loading
 */
export const askAgent = async ({
  agentId,
  question,
  screenName,
  companyId,
  userId,
  extraContext = {},
  overridePrompt = null,
}) => {
  // 1. Get agent config
  const agents = await fetchAgents(companyId);
  const agent = agents.find(a => a.agent_id === agentId);
  if (!agent) return { text: 'Agent not found.', success: false };

  // 2. Load company data from agent's knowledge sources
  const sources = Array.isArray(agent.knowledge_sources) ? agent.knowledge_sources : [];
  const dbData = await fetchCompanyContext(sources, companyId);

  // 3. Get company profile
  let companyProfile = {};
  try {
    const cp = await AsyncStorage.getItem('company_profile');
    if (cp) companyProfile = JSON.parse(cp);
  } catch {}

  // 4. Build full context
  const contextSummary = buildContextSummary({ ...dbData, ...extraContext });
  const systemPrompt = `${overridePrompt || agent.system_prompt}

Company: ${companyProfile.name || 'Unknown'}
Industry: ${companyProfile.industry || 'N/A'}
Company ID: ${companyId}

Current Business Data:
${contextSummary || 'No data available yet — this is a new company.'}

Today's date: ${new Date().toLocaleDateString('en-GB', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
Respond concisely and specifically based on the data above. If no data, suggest what the company should set up first.`;

  // 5. Call Claude
  const result = await callClaude(
    systemPrompt,
    question,
    agent.model || 'claude-sonnet-4-20250514',
    agent.max_tokens || 1000,
    parseFloat(agent.temperature) || 0.7,
  );

  // 6. Log usage + save conversation (async, non-blocking)
  logUsage(agentId, screenName, companyId, userId, result);
  if (result.success) {
    saveConversation(agentId, screenName, companyId, userId, question, result.text, agent.model);
  }

  return result;
};

/**
 * Quick ask — auto-selects best agent for the screen
 */
export const quickAsk = async (question, screenName, extraContext = {}) => {
  const companyId = await AsyncStorage.getItem('company_id');
  const userId = await AsyncStorage.getItem('user_id');
  const agents = await getAgentsForScreen(screenName, companyId);
  
  if (!agents.length) {
    // Fallback — use a generic prompt
    return callClaude(
      'You are a helpful UK business advisor. Provide concise, actionable advice.',
      question,
    );
  }

  // Pick the highest priority agent for this screen
  const agent = agents.sort((a, b) => (a.priority || 5) - (b.priority || 5))[0];
  
  return askAgent({
    agentId: agent.agent_id,
    question,
    screenName,
    companyId,
    userId,
    extraContext,
  });
};

// ═══════════════════════════════════════════════════════════════════════════════
// REACT HOOK — useAI(screenName)
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * React hook for using AI in any screen
 * 
 * Usage:
 *   const { ask, quickAsk, agents, loading, history } = useAI('FinancialManagementScreen');
 */
export const useAI = (screenName) => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const companyIdRef = useRef(null);
  const userIdRef = useRef(null);

  useEffect(() => {
    const init = async () => {
      companyIdRef.current = await AsyncStorage.getItem('company_id');
      userIdRef.current = await AsyncStorage.getItem('user_id');
      const screenAgents = await getAgentsForScreen(screenName, companyIdRef.current);
      setAgents(screenAgents);
    };
    init();
  }, [screenName]);

  const ask = useCallback(async (question, extraContext = {}, agentId = null) => {
    setLoading(true);
    try {
      const targetAgent = agentId
        ? agentId
        : agents.length > 0
        ? agents[0].agent_id
        : null;

      let result;
      if (targetAgent) {
        result = await askAgent({
          agentId: targetAgent,
          question,
          screenName,
          companyId: companyIdRef.current,
          userId: userIdRef.current,
          extraContext,
        });
      } else {
        result = await callClaude(
          'You are a helpful UK business advisor. Provide concise, actionable advice.',
          question,
        );
      }

      setHistory(prev => [...prev, 
        { role: 'user', content: question, timestamp: Date.now() },
        { role: 'assistant', content: result.text, timestamp: Date.now(), agent: targetAgent },
      ]);

      return result;
    } finally {
      setLoading(false);
    }
  }, [agents, screenName]);

  const clearHistory = useCallback(() => setHistory([]), []);

  return {
    ask,
    quickAsk: (q, ctx) => quickAsk(q, screenName, ctx),
    agents,
    loading,
    history,
    clearHistory,
    companyId: companyIdRef.current,
  };
};

// ═══════════════════════════════════════════════════════════════════════════════
// AI CHAT WIDGET COMPONENT — drop into any screen
// ═══════════════════════════════════════════════════════════════════════════════

// Components imported at top of file

export const AIChatWidget = ({ screenName, visible, onClose, title, extraContext = {} }) => {
  const { ask, agents, loading, history, clearHistory } = useAI(screenName);
  const [input, setInput] = useState('');
  const [selectedAgent, setSelectedAgent] = useState(null);
  const scrollRef = useRef(null);

  const handleSend = async () => {
    if (!input.trim() || loading) return;
    const q = input.trim();
    setInput('');
    await ask(q, extraContext, selectedAgent?.agent_id);
    setTimeout(() => scrollRef.current?.scrollToEnd({ animated: true }), 200);
  };

  const suggestedQuestions = agents.length > 0 ? [
    'Give me a summary of my current data',
    'What are the key issues I should address?',
    'What recommendations do you have?',
    'Compare this month vs last month',
  ] : [];

  return (
    <Modal visible={visible} animationType="slide" presentationStyle="pageSheet">
      <KeyboardAvoidingView style={styles.container} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
        {/* Header */}
        <LinearGradient colors={['#6366F1', '#8B5CF6']} style={styles.header}>
          <TouchableOpacity onPress={onClose} style={styles.closeBtn}>
            <MaterialCommunityIcons name="close" size={24} color="#fff" />
          </TouchableOpacity>
          <View style={{ flex: 1, alignItems: 'center' }}>
            <Text style={styles.headerTitle}>{title || 'AI Assistant'}</Text>
            <Text style={styles.headerSub}>
              {agents.length > 0 ? `${agents[0].name} · ${agents.length} agent${agents.length > 1 ? 's' : ''}` : 'General Assistant'}
            </Text>
          </View>
          <TouchableOpacity onPress={clearHistory} style={styles.closeBtn}>
            <MaterialCommunityIcons name="delete-outline" size={22} color="#fff" />
          </TouchableOpacity>
        </LinearGradient>

        {/* Agent selector */}
        {agents.length > 1 && (
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.agentStrip}>
            {agents.map(a => (
              <TouchableOpacity
                key={a.agent_id}
                style={[styles.agentChip, selectedAgent?.agent_id === a.agent_id && styles.agentChipActive]}
                onPress={() => setSelectedAgent(selectedAgent?.agent_id === a.agent_id ? null : a)}
              >
                <MaterialCommunityIcons name={a.icon || 'robot'} size={14} color={selectedAgent?.agent_id === a.agent_id ? '#fff' : a.color || '#6366F1'} />
                <Text style={[styles.agentChipText, selectedAgent?.agent_id === a.agent_id && { color: '#fff' }]}>{a.name}</Text>
              </TouchableOpacity>
            ))}
          </ScrollView>
        )}

        {/* Messages */}
        <ScrollView ref={scrollRef} style={styles.messages} contentContainerStyle={{ paddingBottom: 20 }}>
          {history.length === 0 && (
            <View style={styles.empty}>
              <MaterialCommunityIcons name="robot-excited" size={48} color="#CBD5E1" />
              <Text style={styles.emptyTitle}>Ask me anything about your business</Text>
              <Text style={styles.emptySub}>I have access to your company's data and can provide specific insights.</Text>
              {suggestedQuestions.map((q, i) => (
                <TouchableOpacity key={i} style={styles.suggestion} onPress={() => { setInput(q); }}>
                  <MaterialCommunityIcons name="lightbulb-outline" size={14} color="#6366F1" />
                  <Text style={styles.suggestionText}>{q}</Text>
                </TouchableOpacity>
              ))}
            </View>
          )}

          {history.map((msg, i) => (
            <View key={i} style={[styles.msgRow, msg.role === 'user' && styles.msgRowUser]}>
              {msg.role === 'assistant' && (
                <View style={styles.avatarAI}>
                  <MaterialCommunityIcons name="robot" size={16} color="#6366F1" />
                </View>
              )}
              <View style={[styles.msgBubble, msg.role === 'user' ? styles.msgUser : styles.msgAI]}>
                <Text style={[styles.msgText, msg.role === 'user' && { color: '#fff' }]}>{msg.content}</Text>
              </View>
            </View>
          ))}

          {loading && (
            <View style={styles.typingRow}>
              <View style={styles.avatarAI}>
                <MaterialCommunityIcons name="robot" size={16} color="#6366F1" />
              </View>
              <View style={styles.typing}>
                <ActivityIndicator size="small" color="#6366F1" />
                <Text style={styles.typingText}>Analysing your data...</Text>
              </View>
            </View>
          )}
        </ScrollView>

        {/* Input */}
        <View style={styles.inputRow}>
          <TextInput
            style={styles.input}
            value={input}
            onChangeText={setInput}
            placeholder="Ask about your business..."
            placeholderTextColor="#94A3B8"
            multiline
            maxLength={2000}
            onSubmitEditing={handleSend}
            returnKeyType="send"
          />
          <TouchableOpacity
            style={[styles.sendBtn, (!input.trim() || loading) && { opacity: 0.4 }]}
            onPress={handleSend}
            disabled={!input.trim() || loading}
          >
            <MaterialCommunityIcons name="send" size={20} color="#fff" />
          </TouchableOpacity>
        </View>
      </KeyboardAvoidingView>
    </Modal>
  );
};

// ─── Styles ─────────────────────────────────────────────────────────────────
const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#F8FAFC' },
  header: { flexDirection: 'row', alignItems: 'center', paddingTop: 50, paddingBottom: 16, paddingHorizontal: 16 },
  headerTitle: { fontSize: 18, fontWeight: '800', color: '#fff' },
  headerSub: { fontSize: 12, color: 'rgba(255,255,255,0.7)', marginTop: 2 },
  closeBtn: { width: 40, height: 40, alignItems: 'center', justifyContent: 'center' },
  agentStrip: { maxHeight: 50, paddingHorizontal: 12, paddingVertical: 8, backgroundColor: '#fff', borderBottomWidth: 1, borderBottomColor: '#E2E8F0' },
  agentChip: { flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 12, paddingVertical: 6, borderRadius: 20, borderWidth: 1, borderColor: '#E2E8F0', marginRight: 8, backgroundColor: '#fff' },
  agentChipActive: { backgroundColor: '#6366F1', borderColor: '#6366F1' },
  agentChipText: { fontSize: 12, fontWeight: '600', color: '#475569' },
  messages: { flex: 1, paddingHorizontal: 16 },
  empty: { alignItems: 'center', paddingTop: 60, paddingHorizontal: 20 },
  emptyTitle: { fontSize: 16, fontWeight: '700', color: '#1E293B', marginTop: 16 },
  emptySub: { fontSize: 13, color: '#64748B', textAlign: 'center', marginTop: 8, lineHeight: 20 },
  suggestion: { flexDirection: 'row', alignItems: 'center', gap: 8, backgroundColor: '#EEF2FF', paddingHorizontal: 16, paddingVertical: 10, borderRadius: 20, marginTop: 10 },
  suggestionText: { fontSize: 13, color: '#4338CA', fontWeight: '500' },
  msgRow: { flexDirection: 'row', marginTop: 12, alignItems: 'flex-end' },
  msgRowUser: { justifyContent: 'flex-end' },
  avatarAI: { width: 28, height: 28, borderRadius: 14, backgroundColor: '#EEF2FF', alignItems: 'center', justifyContent: 'center', marginRight: 8 },
  msgBubble: { maxWidth: '80%', borderRadius: 16, padding: 12 },
  msgUser: { backgroundColor: '#6366F1', borderBottomRightRadius: 4 },
  msgAI: { backgroundColor: '#fff', borderWidth: 1, borderColor: '#E2E8F0', borderBottomLeftRadius: 4 },
  msgText: { fontSize: 14, lineHeight: 20, color: '#1E293B' },
  typingRow: { flexDirection: 'row', alignItems: 'center', marginTop: 12 },
  typing: { flexDirection: 'row', alignItems: 'center', gap: 8, backgroundColor: '#fff', borderRadius: 16, padding: 12, borderWidth: 1, borderColor: '#E2E8F0' },
  typingText: { fontSize: 13, color: '#64748B' },
  inputRow: { flexDirection: 'row', alignItems: 'flex-end', padding: 12, backgroundColor: '#fff', borderTopWidth: 1, borderTopColor: '#E2E8F0' },
  input: { flex: 1, backgroundColor: '#F1F5F9', borderRadius: 20, paddingHorizontal: 16, paddingVertical: 10, fontSize: 15, maxHeight: 100, color: '#1E293B' },
  sendBtn: { width: 40, height: 40, borderRadius: 20, backgroundColor: '#6366F1', alignItems: 'center', justifyContent: 'center', marginLeft: 8 },
});

export default { useAI, askAgent, quickAsk, fetchAgents, getAgentsForScreen, AIChatWidget };
